import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  User,
  LineChart,
  ArrowLeftRight,
  LogOut,
  Shield,
  Briefcase,
  Sparkles,
  Receipt,
  BookOpen,
  Activity,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePortfolio } from "../context/PortfolioContext";

const allLinks = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/my-holdings", label: "My Holdings", icon: User },
  { to: "/members", label: "Members", icon: Users, gate: "members" },
  { to: "/portfolio", label: "Portfolio", icon: Briefcase, gate: "portfolio" },
  { to: "/ledger", label: "Ledger", icon: BookOpen, gate: "ledger" },
  { to: "/nav-history", label: "NAV History", icon: LineChart },
  { to: "/cashflows", label: "Cashflows", icon: ArrowLeftRight },
  { to: "/bonus", label: "Bonus", icon: Sparkles },
  { to: "/profit-loss", label: "Profit & Loss", icon: Receipt },
];

const adminLink = { to: "/admin", label: "Admin", icon: Shield };

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const portfolioCtx = usePortfolio();
  const settings = portfolioCtx?.settings || {
    membersPageVisibility: "all",
    portfolioPageVisibility: "all",
    ledgerPageVisibility: "all",
  };
  const portfolio = portfolioCtx?.summary;
  const navigate = useNavigate();

  const isAdmin = user?.role === "admin";
  const links = allLinks.filter((l) => {
    if (isAdmin) return true;
    if (l.gate === "members" && settings.membersPageVisibility === "admin") return false;
    if (l.gate === "portfolio" && settings.portfolioPageVisibility === "admin") return false;
    if (l.gate === "ledger" && settings.ledgerPageVisibility === "admin") return false;
    return true;
  });

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const liveNav = portfolio?.currentNAV ?? null;

  return (
    <div className="app-bg min-h-screen text-slate-100">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-60 bg-slate-950/70 backdrop-blur-xl border-r border-slate-800/80 flex-col z-20">
        {/* Brand */}
        <div className="px-5 py-5 flex items-center gap-3 border-b border-slate-800/70 relative">
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_admin-portal-515/artifacts/63zm8410_DAASMIN%20FINAL%20LOGO%20FOR%20TRADEMARK%20%281%29.png"
              alt="Daasmin"
              className="w-9 h-9 rounded-lg bg-white p-1 relative z-10"
            />
            <div className="absolute -inset-1 rounded-xl bg-emerald-500/20 blur-md -z-0" aria-hidden />
          </div>
          <div className="leading-tight">
            <div className="font-display text-sm tracking-wide brand-gradient">DAASMIN</div>
            <div className="text-[9.5px] text-slate-500 uppercase tracking-[0.18em] font-mono">Family Office</div>
          </div>
        </div>

        {/* Live status pill */}
        <div className="px-5 pt-4 pb-1">
          <div className="flex items-center justify-between rounded-lg bg-slate-900/70 border border-slate-800/80 px-2.5 py-1.5">
            <div className="flex items-center gap-2">
              <span className="pulse-dot" aria-hidden />
              <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/90 font-mono">Live</span>
            </div>
            <div className="text-[10.5px] font-mono text-slate-300 tabular-nums" title="Current NAV">
              {liveNav != null ? `₹${Number(liveNav).toFixed(4)}` : "—"}
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-emerald-500/15 via-emerald-500/8 to-transparent text-emerald-300 nav-glow"
                    : "text-slate-400 hover:bg-slate-900/70 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.7)]" aria-hidden />
                  )}
                  <l.icon size={16} className={isActive ? "drop-shadow-[0_0_6px_rgba(16,185,129,0.6)]" : ""} />
                  <span className="font-tech">{l.label}</span>
                </>
              )}
            </NavLink>
          ))}

          {user?.role === "admin" && (
            <>
              <div className="pt-4 pb-2 px-3 flex items-center gap-2 text-[10px] text-slate-600 uppercase tracking-[0.22em] font-mono">
                <span className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
                Admin
                <span className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
              </div>
              <NavLink
                to={adminLink.to}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-r from-amber-500/15 via-amber-500/8 to-transparent text-amber-300 nav-glow-amber"
                      : "text-slate-400 hover:bg-slate-900/70 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.7)]" aria-hidden />
                    )}
                    <adminLink.icon size={16} className={isActive ? "drop-shadow-[0_0_6px_rgba(245,158,11,0.6)]" : ""} />
                    <span className="font-tech">{adminLink.label}</span>
                  </>
                )}
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-3 border-t border-slate-800/70">
          <div className="px-2 pb-3 flex items-center gap-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-1 ring-emerald-500/30" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 ring-1 ring-emerald-500/30 flex items-center justify-center text-slate-300 text-xs font-medium">
                {(user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">Signed in as</div>
              <div className="text-sm text-white truncate font-tech">{user?.name}</div>
              {user?.role === "admin" && (
                <span className="inline-flex items-center gap-1 mt-1 text-[9.5px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 font-mono uppercase tracking-wider">
                  <Activity size={9} /> Admin
                </span>
              )}
            </div>
          </div>
          <button
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-900/70 hover:text-white transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Top bar on mobile */}
      <header className="md:hidden sticky top-0 z-20 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/70 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_admin-portal-515/artifacts/63zm8410_DAASMIN%20FINAL%20LOGO%20FOR%20TRADEMARK%20%281%29.png"
            alt="Daasmin"
            className="w-8 h-8 rounded-lg bg-white p-1"
          />
          <div>
            <div className="font-display text-sm leading-tight brand-gradient">DAASMIN</div>
            <div className="text-[10px] text-slate-500 font-mono flex items-center gap-1.5">
              <span className="pulse-dot" style={{ width: 6, height: 6 }} /> {user?.name}
            </div>
          </div>
        </div>
        <button onClick={doLogout} className="text-slate-400 p-2" aria-label="Sign out">
          <LogOut size={18} />
        </button>
      </header>

      {/* Content */}
      <main className="md:pl-60 pb-24 md:pb-6">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 md:py-8">{children}</div>
      </main>

      {/* Bottom nav on mobile */}
      <nav
        className={`md:hidden fixed bottom-0 left-0 right-0 z-20 bg-slate-950/85 backdrop-blur-xl border-t border-slate-800/80 grid overflow-x-auto no-scrollbar`}
        style={{ gridTemplateColumns: `repeat(${links.length + (isAdmin ? 1 : 0)}, minmax(0, 1fr))` }}
      >
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2.5 gap-1 text-[10px] transition-colors ${
                isActive ? "text-emerald-400" : "text-slate-500"
              }`
            }
          >
            <l.icon size={18} />
            <span className="leading-none font-tech">{l.label.split(" ")[0]}</span>
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink
            to={adminLink.to}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2.5 gap-1 text-[10px] transition-colors ${
                isActive ? "text-amber-400" : "text-slate-500"
              }`
            }
          >
            <adminLink.icon size={18} />
            <span className="leading-none font-tech">Admin</span>
          </NavLink>
        )}
      </nav>
    </div>
  );
}
